import React, { useState, useEffect } from 'react';
import superAdminApiClient from '../../../api/superAdminApiClient';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { Table, Form, Row, Col, Card, Tabs, Tab, Button, Modal, Badge } from 'react-bootstrap';

const OtherPagesListing = () => {
    // Data States
    const [links, setLinks] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loadingLinks, setLoadingLinks] = useState(true);
    const [loadingCategories, setLoadingCategories] = useState(true);
    const [activeTab, setActiveTab] = useState('links');

    // Modals States
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [editingLink, setEditingLink] = useState(null);
    const [editingCategory, setEditingCategory] = useState(null);

    // Form States
    const [linkForm, setLinkForm] = useState({
        page_title: '',
        category_id: '',
        external_link: '',
        status: 'active'
    });
    const [categoryForm, setCategoryForm] = useState({
        name: '',
        status: 'active'
    });

    // Search / Filter States
    const [searchLink, setSearchLink] = useState('');
    const [filterCategory, setFilterCategory] = useState('all');
    const [searchCategory, setSearchCategory] = useState('');

    // Pagination for Links
    const [currentLinkPage, setCurrentLinkPage] = useState(1);
    const linksPerPage = 10;

    // Pagination for Categories
    const [currentCategoryPage, setCurrentCategoryPage] = useState(1);
    const categoriesPerPage = 10;

    // Reset pagination to first page when search/filter changes
    useEffect(() => {
        setCurrentLinkPage(1);
    }, [searchLink, filterCategory]);

    useEffect(() => {
        setCurrentCategoryPage(1);
    }, [searchCategory]);

    // Helper to generate elegant sliding window pagination ranges
    const getPaginationRange = (currentPage, totalPages) => {
        const delta = 1;
        const range = [];
        for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
            range.push(i);
        }
        if (currentPage - delta > 2) {
            range.unshift('...');
        }
        if (currentPage + delta < totalPages - 1) {
            range.push('...');
        }
        range.unshift(1);
        if (totalPages > 1) {
            range.push(totalPages);
        }
        return range;
    };

    useEffect(() => {
        fetchLinks();
        fetchCategories();
    }, []);

    const fetchLinks = async () => {
        setLoadingLinks(true);
        try {
            const response = await superAdminApiClient.get('/superadmin/web-pages/other-links');
            setLinks(response.data.data);
            setLoadingLinks(false);
        } catch (error) {
            toast.error("Failed to fetch external links");
            setLoadingLinks(false);
        }
    };

    const fetchCategories = async () => {
        setLoadingCategories(true);
        try {
            const response = await superAdminApiClient.get('/superadmin/web-pages/other-categories');
            setCategories(response.data.data);
            setLoadingCategories(false);
        } catch (error) {
            toast.error("Failed to fetch categories");
            setLoadingCategories(false);
        }
    };

    // ==========================================
    // CATEGORY HANDLERS
    // ==========================================
    const handleCategoryStatusToggle = async (id) => {
        try {
            const response = await superAdminApiClient.post('/superadmin/web-pages/other-categories/status', { id });
            if (response.data.success) {
                toast.success(response.data.message);
                setCategories(categories.map(cat => 
                    cat.id === id ? { ...cat, status: cat.status === 'active' ? 'inactive' : 'active' } : cat
                ));
            }
        } catch (error) {
            toast.error("Failed to update category status");
        }
    };

    const handleCategoryDelete = async (id) => {
        Swal.fire({
            title: 'Delete Category?',
            text: "Warning: Deleting this category will also permanently delete all associated external links! This cannot be undone.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete all!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await superAdminApiClient.delete(`/superadmin/web-pages/other-categories/${id}`);
                    if (response.data.success) {
                        Swal.fire('Deleted!', 'Category and associated links deleted successfully.', 'success');
                        setCategories(categories.filter(cat => cat.id !== id));
                        // Also clear deleted category links from state
                        setLinks(links.filter(link => link.category_id !== id));
                    }
                } catch (error) {
                    toast.error("Failed to delete category");
                }
            }
        });
    };

    const openAddCategoryModal = () => {
        setEditingCategory(null);
        setCategoryForm({ name: '', status: 'active' });
        setShowCategoryModal(true);
    };

    const openEditCategoryModal = (cat) => {
        setEditingCategory(cat);
        setCategoryForm({ name: cat.name, status: cat.status });
        setShowCategoryModal(true);
    };

    const handleCategorySubmit = async (e) => {
        e.preventDefault();
        if (!categoryForm.name.trim()) {
            toast.error("Category name is required");
            return;
        }

        try {
            let response;
            if (editingCategory) {
                response = await superAdminApiClient.put(`/superadmin/web-pages/other-categories/${editingCategory.id}`, categoryForm);
            } else {
                response = await superAdminApiClient.post('/superadmin/web-pages/other-categories', categoryForm);
            }

            if (response.data.success) {
                toast.success(editingCategory ? "Category updated successfully!" : "Category created successfully!");
                setShowCategoryModal(false);
                fetchCategories();
                fetchLinks(); // Recalculate categories linked
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to save category");
        }
    };


    // ==========================================
    // LINK HANDLERS
    // ==========================================
    const handleLinkStatusToggle = async (id) => {
        try {
            const response = await superAdminApiClient.post('/superadmin/web-pages/other-links/status', { id });
            if (response.data.success) {
                toast.success(response.data.message);
                setLinks(links.map(link => 
                    link.id === id ? { ...link, status: link.status === 'active' ? 'inactive' : 'active' } : link
                ));
            }
        } catch (error) {
            toast.error("Failed to update link status");
        }
    };

    const handleLinkDelete = async (id) => {
        Swal.fire({
            title: 'Delete External Link?',
            text: "Are you sure you want to delete this link? It will be removed from the footer immediately.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await superAdminApiClient.delete(`/superadmin/web-pages/other-links/${id}`);
                    if (response.data.success) {
                        Swal.fire('Deleted!', 'Link has been successfully deleted.', 'success');
                        setLinks(links.filter(link => link.id !== id));
                    }
                } catch (error) {
                    toast.error("Failed to delete external link");
                }
            }
        });
    };

    const openAddLinkModal = () => {
        const activeCats = categories.filter(c => c.status === 'active');
        setEditingLink(null);
        setLinkForm({
            page_title: '',
            category_id: activeCats.length > 0 ? activeCats[0].id : '',
            external_link: '',
            status: 'active'
        });
        setShowLinkModal(true);
    };

    const openEditLinkModal = (link) => {
        setEditingLink(link);
        setLinkForm({
            page_title: link.page_title,
            category_id: link.category_id,
            external_link: link.external_link,
            status: link.status
        });
        setShowLinkModal(true);
    };

    const handleLinkSubmit = async (e) => {
        e.preventDefault();
        const { page_title, category_id, external_link } = linkForm;

        if (!page_title.trim() || !category_id || !external_link.trim()) {
            toast.error("All fields are required");
            return;
        }

        try {
            let response;
            if (editingLink) {
                response = await superAdminApiClient.put(`/superadmin/web-pages/other-links/${editingLink.id}`, linkForm);
            } else {
                response = await superAdminApiClient.post('/superadmin/web-pages/other-links', linkForm);
            }

            if (response.data.success) {
                toast.success(editingLink ? "Link updated successfully!" : "Link created successfully!");
                setShowLinkModal(false);
                fetchLinks();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to save link");
        }
    };


    // ==========================================
    // FILTER & PAGINATION LOGIC
    // ==========================================
    const filteredLinks = links.filter(link => {
        const matchesSearch = link.page_title.toLowerCase().includes(searchLink.toLowerCase()) || 
                             link.external_link.toLowerCase().includes(searchLink.toLowerCase());
        const matchesCategory = filterCategory === 'all' || link.category_id === parseInt(filterCategory);
        return matchesSearch && matchesCategory;
    });

    const filteredCategories = categories.filter(cat => {
        return cat.name.toLowerCase().includes(searchCategory.toLowerCase());
    });

    const activeCategories = categories.filter(c => c.status === 'active');

    // Slice Links for Pagination
    const indexOfLastLink = currentLinkPage * linksPerPage;
    const indexOfFirstLink = indexOfLastLink - linksPerPage;
    const currentLinksSubset = filteredLinks.slice(indexOfFirstLink, indexOfLastLink);
    const totalLinkPages = Math.ceil(filteredLinks.length / linksPerPage);

    // Slice Categories for Pagination
    const indexOfLastCategory = currentCategoryPage * categoriesPerPage;
    const indexOfFirstCategory = indexOfLastCategory - categoriesPerPage;
    const currentCategoriesSubset = filteredCategories.slice(indexOfFirstCategory, indexOfLastCategory);
    const totalCategoryPages = Math.ceil(filteredCategories.length / categoriesPerPage);

    // Safeguard: Adjust current page if the list shrinks (e.g., on deletion or filtering)
    useEffect(() => {
        if (totalLinkPages > 0 && currentLinkPage > totalLinkPages) {
            setCurrentLinkPage(totalLinkPages);
        }
    }, [filteredLinks.length, totalLinkPages, currentLinkPage]);

    useEffect(() => {
        if (totalCategoryPages > 0 && currentCategoryPage > totalCategoryPages) {
            setCurrentCategoryPage(totalCategoryPages);
        }
    }, [filteredCategories.length, totalCategoryPages, currentCategoryPage]);

    return (
        <div className="container-fluid">
            {/* Header */}
            <div className="row">
                <div className="col-12">
                    <div className="page-title-box d-sm-flex align-items-center justify-content-between m-0 mb-3">
                        <h4 className="mb-sm-0 font-weight-bold text-dark">Other Pages & Links</h4>
                        <div>
                            {activeTab === 'links' ? (
                                <Button 
                                    variant="primary" 
                                    className="d-flex align-items-center gap-1"
                                    onClick={openAddLinkModal}
                                    disabled={activeCategories.length === 0}
                                >
                                    <i className="ri-add-line fs-16"></i> Add External Link
                                </Button>
                            ) : (
                                <Button 
                                    variant="primary" 
                                    className="d-flex align-items-center gap-1"
                                    onClick={openAddCategoryModal}
                                >
                                    <i className="ri-add-line fs-16"></i> Add Category
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Layout Workspace with Tabs */}
            <Card className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                <Card.Body className="p-0">
                    <Tabs
                        id="other-pages-workspace-tabs"
                        activeKey={activeTab}
                        onSelect={(k) => setActiveTab(k)}
                        className="nav-tabs-custom nav-success px-4 pt-3 text-dark font-weight-semibold"
                        style={{ borderBottom: '1px solid #e9ecef' }}
                    >
                        <Tab eventKey="links" title={<><i className="ri-links-line me-1"></i> External Footer Links</>}>
                            {/* TAB 1: Links Listing */}
                            <div className="p-4">
                                {/* Filters */}
                                <Row className="g-3 mb-4">
                                    <Col md={7} lg={8}>
                                        <Form.Control
                                            type="text"
                                            placeholder="Search by link title or URL..."
                                            value={searchLink}
                                            onChange={(e) => setSearchLink(e.target.value)}
                                            className="bg-light border-0"
                                            style={{ boxShadow: 'none', height: '38px' }}
                                        />
                                    </Col>
                                    <Col md={5} lg={4}>
                                        <Form.Select
                                            value={filterCategory}
                                            onChange={(e) => setFilterCategory(e.target.value)}
                                            className="bg-light border-0 text-capitalize"
                                            style={{ boxShadow: 'none', height: '38px' }}
                                        >
                                            <option value="all">All Categories</option>
                                            {categories.map((cat) => (
                                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                                            ))}
                                        </Form.Select>
                                    </Col>
                                </Row>

                                {activeCategories.length === 0 && (
                                    <div className="alert alert-warning border-warning-subtle" role="alert">
                                        <strong>No active categories found!</strong> You must create and activate at least one category in the <strong>"Categories"</strong> tab before managing external links.
                                    </div>
                                )}

                                {/* Links Table */}
                                <div className="table-responsive">
                                    <Table className="table align-middle table-nowrap mb-0 table-hover">
                                        <thead className="table-light text-muted">
                                            <tr>
                                                <th className="py-3 px-4">Title</th>
                                                <th>Category</th>
                                                <th>External Link URL</th>
                                                <th>Status</th>
                                                <th>Created Date</th>
                                                <th className="text-center px-4" style={{ width: '120px' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="text-dark">
                                            {loadingLinks ? (
                                                <tr>
                                                    <td colSpan="6" className="text-center py-5">
                                                        <div className="spinner-border text-primary" role="status">
                                                            <span className="visually-hidden">Loading...</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : currentLinksSubset.length === 0 ? (
                                                <tr>
                                                    <td colSpan="6" className="text-center py-5 text-muted">
                                                        <i className="ri-link-unlink-m fs-36 d-block mb-2 text-secondary"></i>
                                                        No external links found.
                                                    </td>
                                                </tr>
                                            ) : currentLinksSubset.map((link) => (
                                                <tr key={link.id}>
                                                    <td className="font-weight-medium text-dark px-4">
                                                        {link.page_title}
                                                    </td>
                                                    <td>
                                                        <Badge bg="light" className="text-primary border border-primary-subtle py-1.5 px-2.5 fs-12 text-capitalize">
                                                            {link.Category?.name || 'Uncategorized'}
                                                        </Badge>
                                                    </td>
                                                    <td style={{ maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        <a href={link.external_link} target="_blank" rel="noopener noreferrer" className="text-decoration-none text-info">
                                                            {link.external_link} <i className="ri-external-link-line fs-12"></i>
                                                        </a>
                                                    </td>
                                                    <td>
                                                        <div className="form-check form-switch form-switch-md">
                                                            <Form.Check 
                                                                type="switch"
                                                                id={`link-status-switch-${link.id}`}
                                                                checked={link.status === 'active'}
                                                                onChange={() => handleLinkStatusToggle(link.id)}
                                                                style={{ cursor: 'pointer' }}
                                                            />
                                                        </div>
                                                    </td>
                                                    <td className="text-secondary fs-13">
                                                        {new Date(link.created_at || link.createdAt).toLocaleDateString('en-US', {
                                                            year: 'numeric',
                                                            month: 'short',
                                                            day: 'numeric'
                                                        })}
                                                    </td>
                                                    <td className="px-4">
                                                        <div className="d-flex gap-2 justify-content-center">
                                                            <button onClick={() => openEditLinkModal(link)} className="btn btn-sm btn-light border" style={{ padding: '0.35rem 0.55rem' }}>
                                                                <i className="ri-pencil-fill text-muted fs-14"></i>
                                                            </button>
                                                            <button 
                                                                onClick={() => handleLinkDelete(link.id)} 
                                                                className="btn btn-sm btn-light border text-danger" 
                                                                style={{ padding: '0.35rem 0.55rem' }}
                                                            >
                                                                <i className="ri-delete-bin-fill fs-14"></i>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>

                                {/* Links Pagination Controls */}
                                {totalLinkPages > 1 && (
                                    <div className="d-flex align-items-center justify-content-between mt-4 px-2">
                                        <div className="text-secondary fs-13">
                                            Showing {indexOfFirstLink + 1} to {Math.min(indexOfLastLink, filteredLinks.length)} of {filteredLinks.length} entries
                                        </div>
                                        <div className="pagination pagination-rounded justify-content-end mb-0 gap-1">
                                            <button 
                                                className={`btn btn-sm btn-light border ${currentLinkPage === 1 ? 'disabled' : ''}`}
                                                onClick={() => setCurrentLinkPage(prev => Math.max(prev - 1, 1))}
                                                disabled={currentLinkPage === 1}
                                            >
                                                <i className="ri-arrow-left-s-line fs-14"></i> Prev
                                            </button>
                                            {getPaginationRange(currentLinkPage, totalLinkPages).map((pageNo, idx) => (
                                                pageNo === '...' ? (
                                                    <span key={`link-ellipsis-${idx}`} className="px-2 py-1 align-self-center text-muted">...</span>
                                                ) : (
                                                    <button
                                                        key={`link-page-${pageNo}`}
                                                        className={`btn btn-sm border px-3 ${currentLinkPage === pageNo ? 'btn-primary' : 'btn-light'}`}
                                                        onClick={() => setCurrentLinkPage(pageNo)}
                                                    >
                                                        {pageNo}
                                                    </button>
                                                )
                                            ))}
                                            <button 
                                                className={`btn btn-sm btn-light border ${currentLinkPage === totalLinkPages ? 'disabled' : ''}`}
                                                onClick={() => setCurrentLinkPage(prev => Math.min(prev + 1, totalLinkPages))}
                                                disabled={currentLinkPage === totalLinkPages}
                                            >
                                                Next <i className="ri-arrow-right-s-line fs-14"></i>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Tab>

                        <Tab eventKey="categories" title={<><i className="ri-folder-open-line me-1"></i> Dynamic Categories</>}>
                            {/* TAB 2: Categories Listing */}
                            <div className="p-4">
                                {/* Search Category */}
                                <Row className="g-3 mb-4">
                                    <Col xl={12}>
                                        <Form.Control
                                            type="text"
                                            placeholder="Search categories by name..."
                                            value={searchCategory}
                                            onChange={(e) => setSearchCategory(e.target.value)}
                                            className="bg-light border-0"
                                            style={{ boxShadow: 'none', height: '38px' }}
                                        />
                                    </Col>
                                </Row>

                                {/* Categories Table */}
                                <div className="table-responsive">
                                    <Table className="table align-middle table-nowrap mb-0 table-hover">
                                        <thead className="table-light text-muted">
                                            <tr>
                                                <th className="py-3 px-4">Category Name</th>
                                                <th>Linked Links Count</th>
                                                <th>Status</th>
                                                <th>Created Date</th>
                                                <th className="text-center px-4" style={{ width: '120px' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="text-dark">
                                            {loadingCategories ? (
                                                <tr>
                                                    <td colSpan="5" className="text-center py-5">
                                                        <div className="spinner-border text-primary" role="status">
                                                            <span className="visually-hidden">Loading...</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : currentCategoriesSubset.length === 0 ? (
                                                <tr>
                                                    <td colSpan="5" className="text-center py-5 text-muted">
                                                        <i className="ri-folder-reduce-line fs-36 d-block mb-2 text-secondary"></i>
                                                        No categories found.
                                                    </td>
                                                </tr>
                                            ) : currentCategoriesSubset.map((cat) => {
                                                const linkedCount = links.filter(l => l.category_id === cat.id).length;
                                                return (
                                                    <tr key={cat.id}>
                                                        <td className="font-weight-semibold text-dark px-4 text-capitalize">
                                                            {cat.name}
                                                        </td>
                                                        <td>
                                                            <span className="badge bg-info-subtle text-info border border-info-subtle py-1.5 px-3 fs-12">
                                                                {linkedCount} Link{linkedCount !== 1 ? 's' : ''}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="form-check form-switch form-switch-md">
                                                                <Form.Check 
                                                                    type="switch"
                                                                    id={`category-status-switch-${cat.id}`}
                                                                    checked={cat.status === 'active'}
                                                                    onChange={() => handleCategoryStatusToggle(cat.id)}
                                                                    style={{ cursor: 'pointer' }}
                                                                />
                                                            </div>
                                                        </td>
                                                        <td className="text-secondary fs-13">
                                                            {new Date(cat.created_at || cat.createdAt).toLocaleDateString('en-US', {
                                                                year: 'numeric',
                                                                month: 'short',
                                                                day: 'numeric'
                                                            })}
                                                        </td>
                                                        <td className="px-4">
                                                            <div className="d-flex gap-2 justify-content-center">
                                                                <button onClick={() => openEditCategoryModal(cat)} className="btn btn-sm btn-light border" style={{ padding: '0.35rem 0.55rem' }}>
                                                                    <i className="ri-pencil-fill text-muted fs-14"></i>
                                                                </button>
                                                                <button 
                                                                    onClick={() => handleCategoryDelete(cat.id)} 
                                                                    className="btn btn-sm btn-light border text-danger" 
                                                                    style={{ padding: '0.35rem 0.55rem' }}
                                                                >
                                                                    <i className="ri-delete-bin-fill fs-14"></i>
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>

                                {/* Categories Pagination Controls */}
                                {totalCategoryPages > 1 && (
                                    <div className="d-flex align-items-center justify-content-between mt-4 px-2">
                                        <div className="text-secondary fs-13">
                                            Showing {indexOfFirstCategory + 1} to {Math.min(indexOfLastCategory, filteredCategories.length)} of {filteredCategories.length} entries
                                        </div>
                                        <div className="pagination pagination-rounded justify-content-end mb-0 gap-1">
                                            <button 
                                                className={`btn btn-sm btn-light border ${currentCategoryPage === 1 ? 'disabled' : ''}`}
                                                onClick={() => setCurrentCategoryPage(prev => Math.max(prev - 1, 1))}
                                                disabled={currentCategoryPage === 1}
                                            >
                                                <i className="ri-arrow-left-s-line fs-14"></i> Prev
                                            </button>
                                            {getPaginationRange(currentCategoryPage, totalCategoryPages).map((pageNo, idx) => (
                                                pageNo === '...' ? (
                                                    <span key={`cat-ellipsis-${idx}`} className="px-2 py-1 align-self-center text-muted">...</span>
                                                ) : (
                                                    <button
                                                        key={`cat-page-${pageNo}`}
                                                        className={`btn btn-sm border px-3 ${currentCategoryPage === pageNo ? 'btn-primary' : 'btn-light'}`}
                                                        onClick={() => setCurrentCategoryPage(pageNo)}
                                                    >
                                                        {pageNo}
                                                    </button>
                                                )
                                            ))}
                                            <button 
                                                className={`btn btn-sm btn-light border ${currentCategoryPage === totalCategoryPages ? 'disabled' : ''}`}
                                                onClick={() => setCurrentCategoryPage(prev => Math.min(prev + 1, totalCategoryPages))}
                                                disabled={currentCategoryPage === totalCategoryPages}
                                            >
                                                Next <i className="ri-arrow-right-s-line fs-14"></i>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Tab>
                    </Tabs>
                </Card.Body>
            </Card>

            {/* MODAL 1: ADD/EDIT EXTERNAL LINK */}
            <Modal show={showLinkModal} onHide={() => setShowLinkModal(false)} centered className="text-dark">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="font-weight-bold text-dark">
                        {editingLink ? 'Edit External Link' : 'Add New External Link'}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleLinkSubmit}>
                    <Modal.Body className="pt-3">
                        <Form.Group className="mb-3">
                            <Form.Label className="fs-14 font-weight-semibold text-dark">Link / Page Title <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="e.g. Travel Insurance, Flight Bookings"
                                value={linkForm.page_title}
                                onChange={(e) => setLinkForm({ ...linkForm, page_title: e.target.value })}
                                required
                                className="text-dark"
                                style={{ border: '1px solid #ced4da' }}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fs-14 font-weight-semibold text-dark">Category Section <span className="text-danger">*</span></Form.Label>
                            <Form.Select
                                value={linkForm.category_id}
                                onChange={(e) => setLinkForm({ ...linkForm, category_id: e.target.value })}
                                required
                                className="text-dark text-capitalize"
                                style={{ border: '1px solid #ced4da' }}
                            >
                                {activeCategories.map((cat) => (
                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fs-14 font-weight-semibold text-dark">External Target URL <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="url"
                                placeholder="https://example.com/destinations"
                                value={linkForm.external_link}
                                onChange={(e) => setLinkForm({ ...linkForm, external_link: e.target.value })}
                                required
                                className="text-dark"
                                style={{ border: '1px solid #ced4da' }}
                            />
                            <Form.Text className="text-muted">
                                Please enter the complete absolute URL including <code>https://</code>
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-1">
                            <Form.Check 
                                type="switch"
                                id="modal-link-status"
                                label="Active Status"
                                checked={linkForm.status === 'active'}
                                onChange={(e) => setLinkForm({ ...linkForm, status: e.target.checked ? 'active' : 'inactive' })}
                                style={{ cursor: 'pointer' }}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0">
                        <Button variant="light" className="border px-4" onClick={() => setShowLinkModal(false)}>Cancel</Button>
                        <Button variant="primary" className="px-4" type="submit">
                            <i className="ri-save-line fs-14 me-1 align-middle"></i> Save Link
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* MODAL 2: ADD/EDIT CATEGORY */}
            <Modal show={showCategoryModal} onHide={() => setShowCategoryModal(false)} centered className="text-dark">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="font-weight-bold text-dark">
                        {editingCategory ? 'Edit Category' : 'Add New Category'}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleCategorySubmit}>
                    <Modal.Body className="pt-3">
                        <Form.Group className="mb-3">
                            <Form.Label className="fs-14 font-weight-semibold text-dark">Category Section Title <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="e.g. Wellness in India, Flights, Leisure"
                                value={categoryForm.name}
                                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                                required
                                className="text-dark"
                                style={{ border: '1px solid #ced4da' }}
                            />
                        </Form.Group>

                        <Form.Group className="mb-1">
                            <Form.Check 
                                type="switch"
                                id="modal-category-status"
                                label="Active Status"
                                checked={categoryForm.status === 'active'}
                                onChange={(e) => setCategoryForm({ ...categoryForm, status: e.target.checked ? 'active' : 'inactive' })}
                                style={{ cursor: 'pointer' }}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0">
                        <Button variant="light" className="border px-4" onClick={() => setShowCategoryModal(false)}>Cancel</Button>
                        <Button variant="primary" className="px-4" type="submit">
                            <i className="ri-save-line fs-14 me-1 align-middle"></i> Save Category
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
};

export default OtherPagesListing;
